import { Injectable } from '@angular/core';
import { PatientQuery } from '@modules/patient/state/patient-query';
import { OrderQuery } from '@modules/order/state/order-query';
import { FLOW_RX_STEPS, FlowRxStep } from '@modules/rx-intuitive/models/flow-rx-step';
import { HostPlatformService } from '@shared/services/host-platform.service';
import { IntuitiveFlowService } from '@shared/services/intuitive-flow.service';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { ShellStore } from '@shared/store/shell/shell-store';
import { combineLatest, filter, map, Observable, of, switchMap, take } from 'rxjs';

@Injectable()
export class RxIntuitiveForDoctorFacade {
	readonly isSummaryMode$ = this.intuitiveFlowService.isSummaryMode$;
	readonly flowRxStep$ = this.shellQuery.flowRxStep$;
	readonly isFallbackBannerAvailable$ = this.intuitiveFlowService.isFallbackBannerAvailable$;

	readonly isReadOnlyBannerVisible$ = combineLatest([
		this.shellQuery.isReadOnly$,
		this.shellQuery.isRxSent$,
		this.hostPlatformService.isScanner$,
		this.orderQuery.isDraftSelected$
	]).pipe(
		map(([isReadOnly, isRxSent, isScanner, isDraftSelected]) => {
			return isScanner ? isReadOnly : isReadOnly && isRxSent && !isDraftSelected;
		})
	);

	getInitialStep(): Observable<FlowRxStep> {
		return this.shellQuery.rxConfiguration$.pipe(
			filter(x => !!x),
			take(1),
			switchMap(() => {
				if (this.shellQuery.rxId || this.shellQuery.orderId) {
					return this.shellQuery.rx$.pipe(
						filter(rx => !!rx),
						take(1),
						map(() => FLOW_RX_STEPS.RxForm)
					);
				}

				if (this.shellQuery.patientGuidProvided) {
					// Patient may have been explicitly cleared by user in old mode
					if (this.patientQuery.isPatientCleared) {
						return of(FLOW_RX_STEPS.PatientDetails);
					}

					return this.patientQuery.patient$.pipe(
						filter(patient => !!patient),
						take(1),
						map(() => FLOW_RX_STEPS.ProcedureSelection)
					);
				}

				if (this.shellQuery.clonedFromRxID) {
					return this.shellQuery.clonedRx$.pipe(
						filter(rx => !!rx),
						take(1),
						map(() => (this.shellQuery.rx ? FLOW_RX_STEPS.RxForm : FLOW_RX_STEPS.ProcedureSelection))
					);
				}

				if (this.patientQuery.patient) {
					return of(FLOW_RX_STEPS.ProcedureSelection);
				}

				return of(FLOW_RX_STEPS.PatientDetails);
			})
		);
	}

	constructor(
		private readonly shellQuery: ShellQuery,
		private readonly shellStore: ShellStore,
		private readonly orderQuery: OrderQuery,
		private readonly patientQuery: PatientQuery,
		private readonly hostPlatformService: HostPlatformService,
		private readonly intuitiveFlowService: IntuitiveFlowService
	) {}

	updateFlowRxStep(flowRxStep: FlowRxStep): void {
		this.shellStore.update({ flowRxStep });
	}

	resetFlowRxStep(): void {
		this.shellStore.update({ flowRxStep: undefined });
	}
}
